import {
  Component,
  Input,
  Optional,
  Self,
  signal,
  computed,
  HostListener,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl, FormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@dental-ui/shared/i18n';
import { Subscription } from 'rxjs';

interface PanelStyle {
  top: string;
  left: string;
}

interface CalendarCell {
  date: Date;
  current: boolean; // bu ay mı, yoksa önceki/sonraki ay mı
}

type CalendarView = 'days' | 'months' | 'years';

const MIN_YEAR = 1900;
const MAX_YEAR = 2200;
const YEARS_PER_PAGE = 12;

@Component({
  selector: 'lib-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
})
export class DatePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private langSub?: Subscription;

  @Input() label = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() errorMessage = '';

  readonly datePlaceholder = signal('gg.aa.yyyy');

  readonly inputId = `dp-input-${Math.random().toString(36).substring(2, 9)}`;

  readonly isOpen = signal(false);
  readonly panelAbove = signal(false);
  readonly panelStyle = signal<PanelStyle>({ top: '0px', left: '0px' });
  readonly viewYear = signal(new Date().getFullYear());
  readonly viewMonth = signal(new Date().getMonth());
  readonly calendarView = signal<CalendarView>('days');
  readonly yearPageStart = signal(
    Math.floor(new Date().getFullYear() / YEARS_PER_PAGE) * YEARS_PER_PAGE,
  );
  readonly selectedDate = signal<Date | null>(null);

  /** Raw text in the input field — user edits this directly */
  inputText = '';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (_value: string) => {
    // ControlValueAccessor interface requirement
  };
  private onTouched = () => {
    // ControlValueAccessor interface requirement
  };

  readonly WEEKDAYS = ['Pt', 'Sa', 'Çr', 'Pe', 'Cu', 'Ct', 'Pz'];
  readonly MONTHS = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ];

  private readonly elRef = inject(ElementRef);
  private dismissOnViewportChange?: () => void;

  // ControlValueAccessor requires NgControl via constructor with @Self/@Optional
  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.updatePlaceholder();
    this.registerValidators();
    this.langSub = this.translateService.languageChange.subscribe(() => {
      this.updatePlaceholder();
      this.cdr.markForCheck();
    });
  }

  private registerValidators(): void {
    const control = this.ngControl?.control;
    if (!control) return;

    const existing = control.validator;
    control.setValidators([
      existing ?? (() => null),
      this.createDateValidator(),
    ]);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private createDateValidator(): ValidatorFn {
    return (): ValidationErrors | null => {
      const digits = this.inputText.replace(/\D/g, '');
      if (!digits.length) return null;
      if (digits.length < 8) return { incompleteDate: true };
      if (!this.parseDigitsToDate(digits)) return { invalidDate: true };
      return null;
    };
  }

  private updateControlValidity(): void {
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    this.unbindDismissOnViewportChange();
  }

  private updatePlaceholder(): void {
    const lang = this.translateService.getCurrentLanguage();
    this.datePlaceholder.set(lang === 'tr' ? 'gg.aa.yyyy' : 'dd/mm/yyyy');
  }

  get hasError(): boolean {
    if (this.errorMessage) return true;
    return !!(this.ngControl?.errors && this.ngControl?.touched);
  }

  get errorText(): string {
    const errors = this.ngControl?.errors;

    if (this.ngControl?.touched) {
      if (errors?.['incompleteDate']) {
        return this.translate('COMMON.INCOMPLETE_DATE', 'Please enter a complete date');
      }
      if (errors?.['invalidDate']) {
        return this.translate('COMMON.INVALID_DATE', 'Please enter a valid date');
      }
    }

    if (this.errorMessage) return this.errorMessage;

    if (errors?.['required']) {
      return this.translate('COMMON.REQUIRED', 'This field is required');
    }

    return '';
  }

  private translate(key: string, fallback: string): string {
    const text = this.translateService.getText(key);
    return text !== key ? text : fallback;
  }

  readonly yearPageYears = computed(() => {
    const start = this.yearPageStart();
    return Array.from({ length: YEARS_PER_PAGE }, (_, i) => start + i)
      .filter((y) => y >= MIN_YEAR && y <= MAX_YEAR);
  });

  readonly yearPageLabel = computed(() => {
    const years = this.yearPageYears();
    if (!years.length) return '';
    return years.length === 1 ? `${years[0]}` : `${years[0]} – ${years[years.length - 1]}`;
  });

  readonly calendarDays = computed((): CalendarCell[] => {
    const y = this.viewYear();
    const m = this.viewMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startDow = (first.getDay() + 6) % 7; // Mon = 0

    const cells: CalendarCell[] = [];

    // Önceki ayın sonu
    for (let i = startDow - 1; i >= 0; i--) {
      cells.push({ date: new Date(y, m, -i), current: false });
    }

    // Bu ayın günleri
    for (let d = 1; d <= last.getDate(); d++) {
      cells.push({ date: new Date(y, m, d), current: true });
    }

    // Sonraki ayın başı — toplam 42 hücre dolana kadar
    let next = 1;
    while (cells.length < 42) {
      cells.push({ date: new Date(y, m + 1, next++), current: false });
    }

    return cells;
  });

  /** User typed in the text field — format as DD.MM.YYYY while typing */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsBeforeCursor = input.value
      .slice(0, input.selectionStart ?? 0)
      .replace(/\D/g, '').length;
    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    this.applyFormattedInput(input, digits, digitsBeforeCursor);
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.handleDeleteKey(event);
      return;
    }

    const allowedKeys = [
      'Tab', 'Enter', 'Escape',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End',
    ];
    if (allowedKeys.includes(event.key)) return;

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = event.target as HTMLInputElement;
    const pos = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? pos;
    const digits = input.value.replace(/\D/g, '');
    const replacing = end > pos;

    if (!replacing && digits.length >= 8) {
      event.preventDefault();
    }
  }

  private handleDeleteKey(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const pos = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? pos;
    const hasSelection = end > pos;

    if (hasSelection) return;

    if (event.key === 'Backspace' && pos > 0 && value[pos - 1] === '.') {
      event.preventDefault();
      const digitsBefore = value.slice(0, pos).replace(/\D/g, '').length;
      const allDigits = value.replace(/\D/g, '');
      if (digitsBefore > 0) {
        const newDigits = allDigits.slice(0, digitsBefore - 1) + allDigits.slice(digitsBefore);
        this.applyFormattedInput(input, newDigits, digitsBefore - 1);
      }
      return;
    }

    if (event.key === 'Delete' && pos < value.length && value[pos] === '.') {
      event.preventDefault();
      const digitsBefore = value.slice(0, pos).replace(/\D/g, '').length;
      const allDigits = value.replace(/\D/g, '');
      if (digitsBefore < allDigits.length) {
        const newDigits = allDigits.slice(0, digitsBefore) + allDigits.slice(digitsBefore + 1);
        this.applyFormattedInput(input, newDigits, digitsBefore);
      }
    }
  }

  onInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const pos = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? pos;
    const digitsBefore = input.value.slice(0, pos).replace(/\D/g, '').length;
    const digitsAfter = input.value.slice(end).replace(/\D/g, '');
    const pastedDigits = pasted.replace(/\D/g, '');
    const newDigits = (
      input.value.slice(0, pos).replace(/\D/g, '') +
      pastedDigits +
      digitsAfter
    ).slice(0, 8);

    this.applyFormattedInput(input, newDigits, digitsBefore + pastedDigits.length);
  }

  private applyFormattedInput(
    input: HTMLInputElement,
    digits: string,
    digitCursor: number,
  ): void {
    const formatted = this.formatDigitsAsDate(digits);
    this.inputText = formatted;

    const parsed = digits.length === 8 ? this.parseDigitsToDate(digits) : null;
    if (parsed) {
      this.selectedDate.set(parsed);
      this.viewYear.set(parsed.getFullYear());
      this.viewMonth.set(parsed.getMonth());
      this.onChange(this.dateToIso(parsed));
    } else {
      this.selectedDate.set(null);
      this.onChange('');
    }

    this.updateControlValidity();

    const cursorPos = this.getCursorPositionForDigitIndex(
      Math.min(digitCursor, digits.length),
      formatted,
    );

    requestAnimationFrame(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    });
  }

  private formatDigitsAsDate(digits: string): string {
    const d = digits.slice(0, 8);
    if (!d.length) return '';

    const day = d.slice(0, 2);
    if (d.length <= 2) {
      return d.length === 2 ? `${day}.` : day;
    }

    const monthPart = d.slice(2, 4);
    if (d.length <= 4) {
      return d.length === 4 ? `${day}.${monthPart}.` : `${day}.${monthPart}`;
    }

    return `${day}.${monthPart}.${d.slice(4)}`;
  }

  private getCursorPositionForDigitIndex(digitIndex: number, formatted: string): number {
    if (digitIndex <= 0) return 0;

    let digitsSeen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitsSeen++;
        if (digitsSeen === digitIndex) {
          const hasDotAfter = formatted[i + 1] === '.';
          return hasDotAfter ? i + 2 : i + 1;
        }
      }
    }

    return formatted.length;
  }

  private parseDigitsToDate(digits: string): Date | null {
    const day = +digits.slice(0, 2);
    const month = +digits.slice(2, 4) - 1;
    const year = +digits.slice(4, 8);
    return this.makeDate(year, month, day);
  }

  /** On blur, validate and normalise the text to formatted display value */
  onInputBlur(): void {
    this.onTouched();

    const digits = this.inputText.replace(/\D/g, '');
    const parsed = digits.length === 8 ? this.parseDigitsToDate(digits) : null;

    if (parsed) {
      this.selectedDate.set(parsed);
      this.inputText = this.formatDate(parsed);
      this.onChange(this.dateToIso(parsed));
    } else if (!digits.length) {
      this.selectedDate.set(null);
      this.inputText = '';
      this.onChange('');
    } else {
      this.selectedDate.set(null);
      this.onChange('');
    }

    this.updateControlValidity();
  }

  openCalendar(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    if (!this.isOpen()) {
      this.computePanelPosition();
      const d = this.selectedDate() ?? new Date();
      this.viewYear.set(d.getFullYear());
      this.viewMonth.set(d.getMonth());
      this.calendarView.set('days');
      this.syncYearPageToView();
      this.isOpen.set(true);
      this.bindDismissOnViewportChange();
    } else {
      this.closePanel();
    }
    this.onTouched();
  }

  private closePanel(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.unbindDismissOnViewportChange();
  }

  /** Panel is fixed; close when the page scrolls so it does not detach from the input. */
  private bindDismissOnViewportChange(): void {
    this.unbindDismissOnViewportChange();
    const dismiss = () => this.closePanel();
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    this.dismissOnViewportChange = () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      this.dismissOnViewportChange = undefined;
    };
  }

  private unbindDismissOnViewportChange(): void {
    this.dismissOnViewportChange?.();
  }

  private computePanelPosition(): void {
    const panelWidth = 272;
    const panelHeight = 320;
    const rect = (this.elRef.nativeElement as HTMLElement).getBoundingClientRect();

    // Yatay: sağda yeterli alan yoksa sağa hizala
    const spaceRight = window.innerWidth - rect.left;
    const rawLeft = spaceRight >= panelWidth + 8 ? rect.left : rect.right - panelWidth;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - panelWidth - 8));

    // Dikey: aşağıda yer yoksa yukarı aç
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < panelHeight + 8 && rect.top >= panelHeight + 8;
    this.panelAbove.set(above);
    const top = above ? rect.top - panelHeight - 6 : rect.bottom + 6;

    this.panelStyle.set({ top: `${top}px`, left: `${left}px` });
  }

  showMonthPicker(event: Event): void {
    event.stopPropagation();
    this.calendarView.set('months');
  }

  showYearPicker(event: Event): void {
    event.stopPropagation();
    this.syncYearPageToView();
    this.calendarView.set('years');
  }

  prevHeader(event: Event): void {
    event.stopPropagation();
    const view = this.calendarView();
    if (view === 'days') {
      this.prevMonth();
    } else if (view === 'months') {
      this.viewYear.update((y) => Math.max(MIN_YEAR, y - 1));
    } else {
      this.shiftYearPage(-YEARS_PER_PAGE);
    }
  }

  nextHeader(event: Event): void {
    event.stopPropagation();
    const view = this.calendarView();
    if (view === 'days') {
      this.nextMonth();
    } else if (view === 'months') {
      this.viewYear.update((y) => Math.min(MAX_YEAR, y + 1));
    } else {
      this.shiftYearPage(YEARS_PER_PAGE);
    }
  }

  selectMonth(event: Event, month: number): void {
    event.stopPropagation();
    this.viewMonth.set(month);
    this.calendarView.set('days');
  }

  selectYear(event: Event, year: number): void {
    event.stopPropagation();
    this.viewYear.set(year);
    this.calendarView.set('months');
  }

  private prevMonth(): void {
    if (this.viewMonth() === 0) {
      this.viewMonth.set(11);
      this.viewYear.update((y) => Math.max(MIN_YEAR, y - 1));
    } else {
      this.viewMonth.update((m) => m - 1);
    }
  }

  private nextMonth(): void {
    if (this.viewMonth() === 11) {
      this.viewMonth.set(0);
      this.viewYear.update((y) => Math.min(MAX_YEAR, y + 1));
    } else {
      this.viewMonth.update((m) => m + 1);
    }
  }

  private syncYearPageToView(): void {
    const year = this.viewYear();
    this.yearPageStart.set(
      Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE,
    );
  }

  private shiftYearPage(delta: number): void {
    const next = this.yearPageStart() + delta;
    const maxStart = Math.floor(MAX_YEAR / YEARS_PER_PAGE) * YEARS_PER_PAGE;
    this.yearPageStart.set(Math.max(MIN_YEAR, Math.min(maxStart, next)));
  }

  selectDay(event: Event, day: Date): void {
    event.stopPropagation();
    this.selectedDate.set(day);
    this.inputText = this.formatDate(day);
    this.onChange(this.dateToIso(day));
    this.updateControlValidity();
    // Komşu aya tıklandıysa o aya geç, sonra kapat
    this.viewYear.set(day.getFullYear());
    this.viewMonth.set(day.getMonth());
    this.closePanel();
  }

  isSelected(day: Date): boolean {
    const sel = this.selectedDate();
    return !!sel && sel.toDateString() === day.toDateString();
  }

  isToday(day: Date): boolean {
    return new Date().toDateString() === day.toDateString();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const host = this.elRef.nativeElement as HTMLElement;
    if (!host.contains(e.target as Node)) {
      this.closePanel();
    }
  }

  writeValue(value: string): void {
    if (value) {
      const [y, m, d] = value.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      this.selectedDate.set(date);
      this.inputText = this.formatDate(date);
      this.viewYear.set(y);
      this.viewMonth.set(m - 1);
    } else {
      this.selectedDate.set(null);
      this.inputText = '';
    }
    this.updateControlValidity();
  }

  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  // ── helpers ──────────────────────────────────────────────

  private formatDate(d: Date): string {
    return d.toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  }

  private dateToIso(d: Date): string {
    return [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
    ].join('-');
  }

  /**
   * Accepts:
   *   GG.AA.YYYY  →  01.05.2026
   *   GG/AA/YYYY
   *   GG-AA-YYYY
   *   YYYY-MM-DD  (ISO)
   */
  private parseTextToDate(raw: string): Date | null {
    const s = raw.trim();
    if (!s) return null;

    // ISO: YYYY-MM-DD
    const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
      const y = +isoMatch[1], m = +isoMatch[2] - 1, d = +isoMatch[3];
      return this.makeDate(y, m, d);
    }

    // Turkish / slash / dash: GG.AA.YYYY  GG/AA/YYYY  GG-AA-YYYY
    const trMatch = s.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
    if (trMatch) {
      const d = +trMatch[1], m = +trMatch[2] - 1, y = +trMatch[3];
      return this.makeDate(y, m, d);
    }

    return null;
  }

  private makeDate(y: number, m: number, d: number): Date | null {
    if (y < MIN_YEAR || y > MAX_YEAR || m < 0 || m > 11 || d < 1 || d > 31) return null;
    const date = new Date(y, m, d);
    if (date.getMonth() !== m) return null; // overflow check
    return date;
  }
}
